import React, { useEffect } from 'react';
import { BasisTheoryProvider, useBasisTheory } from '@basis-theory/react-elements';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import config from 'aws-exports';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { offerHasValidOptions, offerUsesAutoUWReview } from 'core/helpers/underwriting-form-helpers';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import { getStalePaymentScheduleDate } from 'offer/helpers/rate-control-date';
import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { OfferNotifications } from 'offer/components/notifications';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import { useBasisTheoryElementRefsProvider } from 'common/hooks/basis-theory-elements.context';
import { CheckoutForm } from './components/checkout-form';
import OptionNotAvailable from './components/option-not-available-modal';
import useCheckoutContextGenerator from './hooks/use-checkout-context-generator';
import CheckoutContext from './checkout.context';

const stripePromise = loadStripe(config.stripeKey);

const Checkout = observer(({ option }) => {
  const history = useHistory();
  const { offer: offerStore } = useStore();
  const { offer, getOffer } = offerStore;
  const { offerId } = useParams();

  // Clean the offerId from URL params to handle trailing punctuation
  const cleanedOfferId = extractCleanOfferId(offerId);

  const { bt } = useBasisTheory(config.basisTheoryApiKey, {
    elements: true,
    environment: config.basisTheoryElementsEnv
  });
  const { Provider: BasisTheoryElementsProvider, refs: elementRefs } = useBasisTheoryElementRefsProvider();
  const checkoutContext = useCheckoutContextGenerator();
  // Destructure `notify` rather than keeping the whole toast object: ToastProvider rebuilds its
  // context value every render, so depending on the object in the effect below would re-fire it
  // on each notify — and notify sets provider state, which would loop. `notify` is a useState
  // setter and therefore stable.
  const { notify } = useToast();

  // Checkout is unreachable until outstanding data pulls have run and — for farm — underwriting
  // has approved. Redirecting without saying why is indistinguishable from a dead "Continue to
  // checkout" button, which is exactly how this surfaced. Derive the reason from the `option`
  // route param rather than offerStore.isFarmOffer, because that store value is only set by the
  // setSelectedOption call further down and this must run before the `!offer` early return.
  const approvalDetails = offer?.quote?.preBindUWCheck?.approvalDetails;
  // Farm and auto UW review offers both require underwriter approval before checkout (#32744).
  // Enforce it here at the checkout entry point too — not just via the disabled footer button — so
  // a direct/bookmarked /checkout URL can't bypass the review. Uses the `option` route param, not
  // offerStore.selectedOption, which isn't set until below.
  const uwUnapproved = !approvalDetails?.underwriterApprovedAt || !approvalDetails?.underwriterApprovedBy;
  const farmNeedsUWApproval = isFarmBasedPolicyType(option) && uwUnapproved;
  const autoNeedsUWApproval = offerUsesAutoUWReview(option) && uwUnapproved;

  // #35629: a mutual-brand offer rated on an earlier day carries a payment schedule starting on the
  // rating day, but billing schedules from the bind day. Resolved from the `option` route param for the
  // same reason as the UW checks above.
  const stalePaymentScheduleDate = getStalePaymentScheduleDate({
    option: offer?.options?.find((o) => o.type === getQuoteFriendlySelectedOption(option)),
    global: offer?.quote?.global,
    rateControlDate: offer?.quote?.global?.rateControlDate,
    state: offerStore.state,
    config: offerStore.rateControlDateConfig
  });

  let blockedReason = null;
  if (offer?.skippedDataPulls?.length > 0) {
    blockedReason = 'Run the outstanding reports before checking out.';
  } else if (offer && farmNeedsUWApproval) {
    blockedReason = 'This farm offer needs underwriting approval before checkout.';
  } else if (offer && autoNeedsUWApproval) {
    blockedReason = 'This auto offer needs underwriting approval before checkout.';
  } else if (stalePaymentScheduleDate) {
    blockedReason = `This offer's payment schedule was calculated on ${stalePaymentScheduleDate}. Update the offer before checking out so payments start from today.`;
  }

  useEffect(() => {
    if (blockedReason) {
      notify({ type: 'error', message: blockedReason });
    }
  }, [blockedReason, notify]);

  let optionNotAvailable = false;

  if (!offer) {
    getOffer(cleanedOfferId);
    return <Loading noBackground />;
  }

  offerStore.setSelectedOption(option);

  if (blockedReason) {
    history.push(`/offer/${offer.id}`);
    return <Loading noBackground />;
  }

  if (offer?.options) {
    const optionsAvailable = offerHasValidOptions({
      offer: offerStore?.offer,
      priorQuoteWithPreBindUWRejections: offerStore.priorQuoteWithPreBindUWRejections,
      rejections: offer?.rejections
    });

    if (!optionsAvailable || !offer.options.some((o) => o.type === getQuoteFriendlySelectedOption(option))) {
      optionNotAvailable = true;
    }
  }

  return (
    <BasisTheoryProvider bt={bt}>
      <BasisTheoryElementsProvider value={elementRefs}>
        <CheckoutContext.Provider value={checkoutContext}>
          <Elements stripe={stripePromise}>
            {optionNotAvailable && <OptionNotAvailable optionNotAvailable={optionNotAvailable} />}
            {offerStore.notifications?.length && <OfferNotifications />}
            <CheckoutForm disabled={optionNotAvailable} />
          </Elements>
        </CheckoutContext.Provider>
      </BasisTheoryElementsProvider>
    </BasisTheoryProvider>
  );
});

export default Checkout;
